export const DEFAULT_PAGE_CONFIG = {
  // Content
  title: 'Thank You!',
  message: 'You have successfully completed the workflow.',
  additional_info: 'We will be in contact with you soon.',
  button_text: 'Done',
  back_button_text: 'Go Back',
  
  // Visibility
  show_icon: true,
  show_logo: false,
  show_title: true,
  show_message: true,
  show_additional_info: true,
  show_dashboard_button: true,
  show_back_button: false,
  show_confetti: false,
  
  // Branding
  logo_url: '',
  logo_size: 'medium',
  
  // Icon
  icon_type: 'check-circle',
  icon_style: 'filled',
  icon_size: 'large',
  
  // Colors
  background_color: '#ffffff',
  background_gradient: false,
  background_gradient_from: '#667eea',
  background_gradient_to: '#764ba2',
  card_background: '#ffffff',
  title_color: '#111827',
  message_color: '#6b7280',
  additional_info_color: '#9ca3af',
  icon_color: '#10b981',
  
  // Primary Button
  primary_button_color: '#10b981',
  primary_button_text_color: '#ffffff',
  primary_button_style: 'filled',
  primary_button_radius: 'pill',
  
  // Secondary Button
  secondary_button_color: '#6b7280',
  secondary_button_text_color: '#6b7280',
  secondary_button_style: 'outline',
  secondary_button_radius: 'pill',
  
  // Typography
  font_family: 'system',
  title_size: 'large',
  message_size: 'medium',
  title_weight: 'bold',
  
  // Layout
  layout: 'centered',
  content_width: 'medium',
  padding: 'comfortable',
  card_shadow: 'medium',
  card_radius: 'large',
  
  // Animation
  animation: 'fade-in',
  
  // Custom CSS
  custom_css: ''
};

export default {
  name: 'PageBuilder',
  props: {
    modelValue: {
      type: Object,
      default: () => ({ ...DEFAULT_PAGE_CONFIG })
    },
    showPreview: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      active_tab: 'content',
      animation_key: 0,
      icon_options: [
        { value: 'check-circle', label: 'Check Circle', icon: 'bi-check-circle-fill' },
        { value: 'check', label: 'Checkmark', icon: 'bi-check-lg' },
        { value: 'heart', label: 'Heart', icon: 'bi-heart-fill' },
        { value: 'star', label: 'Star', icon: 'bi-star-fill' },
        { value: 'trophy', label: 'Trophy', icon: 'bi-trophy-fill' },
        { value: 'gift', label: 'Gift', icon: 'bi-gift-fill' },
        { value: 'rocket', label: 'Rocket', icon: 'bi-rocket-takeoff-fill' },
        { value: 'emoji-smile', label: 'Smile', icon: 'bi-emoji-smile-fill' },
        { value: 'hand-thumbs-up', label: 'Thumbs Up', icon: 'bi-hand-thumbs-up-fill' },
        { value: 'patch-check', label: 'Verified', icon: 'bi-patch-check-fill' }
      ],
      font_options: [
        { value: 'system', label: 'System Default' },
        { value: 'inter', label: 'Inter' },
        { value: 'roboto', label: 'Roboto' },
        { value: 'poppins', label: 'Poppins' },
        { value: 'montserrat', label: 'Montserrat' },
        { value: 'open-sans', label: 'Open Sans' }
      ],
      animation_options: [
        { value: 'none', label: 'None' },
        { value: 'fade-in', label: 'Fade In' },
        { value: 'slide-up', label: 'Slide Up' },
        { value: 'zoom-in', label: 'Zoom In' },
        { value: 'bounce', label: 'Bounce' }
      ]
    };
  },
  computed: {
    config: {
      get() {
        return { ...DEFAULT_PAGE_CONFIG, ...this.modelValue };
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    preview_styles() {
      const c = this.config;
      let bg = c.background_color;
      if (c.background_gradient) {
        bg = `linear-gradient(135deg, ${c.background_gradient_from} 0%, ${c.background_gradient_to} 100%)`;
      }
      return {
        background: bg,
        fontFamily: this.get_font_family(c.font_family)
      };
    },
    card_styles() {
      const c = this.config;
      return {
        background: c.card_background,
        borderRadius: this.get_radius(c.card_radius),
        boxShadow: this.get_shadow(c.card_shadow),
        padding: this.get_padding(c.padding),
        maxWidth: this.get_width(c.content_width)
      };
    },
    icon_class() {
      const c = this.config;
      const style = c.icon_style === 'filled' ? '-fill' : '';
      return `bi-${c.icon_type}${style}`;
    },
    icon_styles() {
      const c = this.config;
      return {
        color: c.icon_color,
        fontSize: this.get_icon_size(c.icon_size)
      };
    },
    title_styles() {
      const c = this.config;
      return {
        color: c.title_color,
        fontSize: this.get_title_size(c.title_size),
        fontWeight: c.title_weight === 'bold' ? '700' : c.title_weight === 'semibold' ? '600' : '400'
      };
    },
    message_styles() {
      const c = this.config;
      return {
        color: c.message_color,
        fontSize: this.get_message_size(c.message_size)
      };
    },
    additional_info_styles() {
      return {
        color: this.config.additional_info_color
      };
    },
    primary_button_styles() {
      const c = this.config;
      const styles = {
        borderRadius: this.get_button_radius(c.primary_button_radius)
      };
      if (c.primary_button_style === 'filled') {
        styles.backgroundColor = c.primary_button_color;
        styles.color = c.primary_button_text_color;
        styles.border = 'none';
      } else {
        styles.backgroundColor = 'transparent';
        styles.color = c.primary_button_color;
        styles.border = `2px solid ${c.primary_button_color}`;
      }
      return styles;
    },
    secondary_button_styles() {
      const c = this.config;
      const styles = {
        borderRadius: this.get_button_radius(c.secondary_button_radius)
      };
      if (c.secondary_button_style === 'filled') {
        styles.backgroundColor = c.secondary_button_color;
        styles.color = c.secondary_button_text_color;
        styles.border = 'none';
      } else {
        styles.backgroundColor = 'transparent';
        styles.color = c.secondary_button_color;
        styles.border = `2px solid ${c.secondary_button_color}`;
      }
      return styles;
    },
    logo_styles() {
      const sizes = { small: '30px', medium: '50px', large: '80px' };
      return {
        maxHeight: sizes[this.config.logo_size] || '50px'
      };
    },
    animation_class() {
      const c = this.config;
      if (c.animation === 'none') return '';
      return `animate-${c.animation}`;
    }
  },
  methods: {
    update_field(field, value) {
      const new_config = { ...this.config, [field]: value };
      this.$emit('update:modelValue', new_config);
    },
    set_tab(tab) {
      this.active_tab = tab;
    },
    select_icon(icon_value) {
      this.update_field('icon_type', icon_value);
    },
    replay_animation() {
      this.animation_key++;
    },
    
    // Helper methods for computed styles
    get_font_family(font) {
      const fonts = {
        system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        inter: '"Inter", sans-serif',
        roboto: '"Roboto", sans-serif',
        poppins: '"Poppins", sans-serif',
        montserrat: '"Montserrat", sans-serif',
        'open-sans': '"Open Sans", sans-serif'
      };
      return fonts[font] || fonts.system;
    },
    get_radius(size) {
      const radii = { none: '0', small: '8px', medium: '12px', large: '20px', xl: '28px' };
      return radii[size] || '12px';
    },
    get_shadow(size) {
      const shadows = {
        none: 'none',
        small: '0 1px 3px rgba(0,0,0,0.1)',
        medium: '0 4px 12px rgba(0,0,0,0.1)',
        large: '0 10px 40px rgba(0,0,0,0.15)'
      };
      return shadows[size] || shadows.medium;
    },
    get_padding(size) {
      const paddings = { compact: '1.5rem', comfortable: '2.5rem', spacious: '3.5rem' };
      return paddings[size] || '2.5rem';
    },
    get_width(size) {
      const widths = { small: '380px', medium: '480px', large: '580px', full: '100%' };
      return widths[size] || '480px';
    },
    get_icon_size(size) {
      const sizes = { small: '2.5rem', medium: '3.5rem', large: '4.5rem', xl: '6rem' };
      return sizes[size] || '3.5rem';
    },
    get_title_size(size) {
      const sizes = { small: '1.25rem', medium: '1.5rem', large: '1.875rem', xl: '2.25rem' };
      return sizes[size] || '1.5rem';
    },
    get_message_size(size) {
      const sizes = { small: '0.813rem', medium: '0.938rem', large: '1.063rem' };
      return sizes[size] || '0.938rem';
    },
    get_button_radius(style) {
      const radii = { square: '4px', rounded: '8px', pill: '50px' };
      return radii[style] || '50px';
    },
    get_icon_display_class(icon_value) {
      const icon = this.icon_options.find(i => i.value === icon_value);
      return icon ? icon.icon : 'bi-check-circle-fill';
    }
  }
};
